import { RouterContext } from '@anm/contexts/RouterContext';
import usePageVisibility from '@anm/hooks/usePageVisibility';
import { uploadsSelectors } from '@anm/store/modules/uploads';
import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useFolderTree from '../../../../hooks/useFolderTree';
import { tabMap } from '../../../../pages/video';
import { appMetaSelectors } from '../../../../store/modules/appMeta';
import { projectSelectors } from '../../../../store/modules/project';
import { userSelectors } from '../../../../store/modules/user';
import { videoFolderActions, videoFolderSelectors } from '../../../../store/modules/videoFolder';

export const useFetchFolderAction = (folderId?: number) => {
  const dispatch = useDispatch();
  const { params } = useContext(RouterContext);
  const { folderPath: folderPathFromURL } = params;
  const { fetchRootFolder, fetchFolderByPath } = videoFolderActions;
  const user = useSelector(userSelectors.selectUser);
  const isProfilePending = useSelector(userSelectors.selectUserPending);
  const workspace = useSelector(appMetaSelectors.selectWorkspace);

  const isPageVisible = usePageVisibility();
  const isUploading = useSelector(uploadsSelectors.selectIsUploadWithProjectCreationPending);
  const isStreamModalOpened = useSelector(projectSelectors.selectIsStreamModalOpen);
  const isDashboard = params.page === '/video' || Object.keys(tabMap).includes(params.tab);

  const isForceLoad = isPageVisible && !isUploading && !isDashboard && !isStreamModalOpened;

  const folderPathFromId = useSelector(state => videoFolderSelectors.getFolderPathFor(state, folderId));

  const ROOT_FOLDER = 'root-wave';
  const folderPath = (folderPathFromURL as string | undefined) || folderPathFromId;
  const canFetchFolderByPath = folderPath && folderPath !== ROOT_FOLDER;

  const fetchFolders = useCallback(() => {
    if (!user.profile || isProfilePending) return;

    canFetchFolderByPath
      ? dispatch(fetchFolderByPath({ path: folderPath!, isForceLoad }))
      : dispatch(fetchRootFolder(isForceLoad));
  }, [folderPath, !!user.profile, isForceLoad, isProfilePending, workspace]);

  return [{ folderPath }, { fetchFolders }] as const;
};

const useFetchFolder = (folderId?: number) => {
  useFolderTree();

  const user = useSelector(userSelectors.selectUser);
  const isProfilePending = useSelector(userSelectors.selectUserPending);
  const [{ folderPath }, { fetchFolders }] = useFetchFolderAction(folderId);
  const isPageVisible = usePageVisibility();

  const isTreePending = useSelector(videoFolderSelectors.selectFolderTreePending);
  const isFolderPending = useSelector(videoFolderSelectors.selectCurrentFolderPendingStatus);

  const isPending = isTreePending || isFolderPending;

  useEffect(() => {
    fetchFolders();
  }, [folderPath, user.profile?.userId, isPageVisible, isProfilePending]);

  return isPending;
};

export default useFetchFolder;
